import { Chip } from '@/ui/Chip/Chip'
import { Link } from '@/ui/Link/Link'
import { Text } from '@/ui/layout/Text'
import { RowListItem } from '@/ui/RowList/RowList'
import { StatusBadge } from '@/ui/Status/Status'
import { kindDisplay } from '../../constants/resourceKinds'
import { ROUTES } from '../../constants/routes'
import { resourcesCopy } from '../../copy/resources'
import { useNow } from '../../hooks/useNow'
import { useResourceKinds } from '../../hooks/useResourceKinds'
import type { TopicResource } from '../../types/api'
import { formatRelative } from '../../utils/time'
import styles from './ResourceRow.module.css'
import { reviewFlag } from './resourceFacets'

interface Props {
  resource: TopicResource
  /** The provenance line (author, age, scope); the thread panel hides it. */
  meta?: boolean
}

/**
 * One resource row (topic tab, workspace index and thread panel share it):
 * kind chip, the name as a link to the resource page, a review badge when
 * the row needs one, and the provenance line right-aligned. Renders an
 * `li`, so callers place rows in a `<RowList variant="plain">`.
 */
export function ResourceRow({ resource, meta = true }: Props) {
  const now = useNow()
  const { data: kinds } = useResourceKinds()
  const kind = kindDisplay(resource.kind, kinds)
  const flag = reviewFlag(resource)
  const authorLabel =
    resource.author?.kind === 'agent'
      ? (resource.author.display_name ?? resourcesCopy.list.agentFallback)
      : resource.author?.kind === 'user'
        ? (resource.author.display_name ?? resourcesCopy.list.someoneFallback)
        : null
  const metaText = [
    authorLabel,
    formatRelative(resource.updated_at, now),
    resource.workspace_id ? resourcesCopy.list.workspaceTag : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <RowListItem>
      <Chip variant="kind" icon={kind.icon} label={kind.label} />
      <Link variant="quiet" href={ROUTES.resourceDetail(resource.id)}>
        {resource.name || resource.kind}
      </Link>
      {flag && (
        <StatusBadge tone="warning">{resourcesCopy.flag[flag]}</StatusBadge>
      )}
      {meta && (
        <Text
          variant="caption"
          tone="text-mute"
          numeric
          className={styles.meta}
        >
          {metaText}
        </Text>
      )}
    </RowListItem>
  )
}
